import { Fetch } from '../../interfaces/fetch'
import { ZKX_API_BASE } from '../../utils/networks'

/**
 * DEX router/factory registry served by the ZKX backend.
 *
 * It maps the contract addresses a swap actually touches (routers, factories,
 * pool managers) to the protocol that owns them, so a sign screen can say
 * "Interacting with PulseX V2" instead of "Interacting with 0x165C34…52d9".
 *
 * The endpoint takes `networks` / `account` / `baseCurrency` query params but
 * IGNORES all three — probed 2026-09-23, every combination returns the same
 * ~18KB document covering all 12 chains. So we fetch it once, whole, and index
 * it locally rather than per chain or per address.
 *
 * NOTE: as of 2026-09-23 it only exists on the dev host; `apis.zkxwallet.com`
 * answers 404. That is why a failure here is a silent no-op with a cooldown:
 * on uat/prod the wallet simply keeps the relayer-derived contract names.
 */

export const DEX_ROUTERS_URL = `${ZKX_API_BASE}/profiles/networks/dex-routers`

/** How long a successfully fetched registry is served before refreshing. */
export const DEX_ROUTERS_TTL_IN_MS = 1000 * 60 * 60 * 24 // 24 hours
/** How long to wait before retrying after a failed/404 fetch. */
export const DEX_ROUTERS_ERROR_COOLDOWN_IN_MS = 1000 * 60 * 30 // 30 minutes

export interface DexRouterEntry {
  /** Display name, e.g. "Uniswap V3" or "Uniswap V3 Factory". */
  name: string
  /** Protocol name without the role suffix, e.g. "Uniswap V3". */
  providerName: string
  /** Which contract of the protocol this address is (`router02`, `factory`…). */
  role: string
  logoURI?: string
}

/** Keyed `${chainId}:${lowercased address}` — the same router address can be a
 * different protocol on another chain, so the chain is part of the key. */
export interface DexRouterIndex {
  [chainAndAddress: string]: DexRouterEntry
}

/**
 * The two storage calls the registry cache needs. Deliberately structural, not
 * `IStorageController`: on non-extension builds the in-process controller is
 * handed a thin localStorage adapter instead of the full storage controller.
 */
export interface DexRoutersStorage {
  get: (key: any, defaultValue?: any) => Promise<any>
  set: (key: any, value: any) => Promise<any>
}

export interface DexRoutersCache {
  fetchedAt: number
  index: DexRouterIndex
}

export interface DexRoutersApiResponse {
  chainId: number
  chainName: string
  dexes: {
    providerName: string
    providerLogoURI?: string
    protocolVersion?: string
    contractAddress: { [role: string]: string }
  }[]
}

/**
 * Role suffixes. Router-shaped roles render as the bare protocol name, because
 * that is the contract a user actually swaps through and "Uniswap V3 Router"
 * adds nothing. Everything else is disambiguated, so the V4 pool manager and
 * the V4 universal router don't both render as "Uniswap V4".
 */
const ROLE_SUFFIXES: { [role: string]: string } = {
  router: '',
  router02: '',
  routerV2: '',
  swapRouter: '',
  swapRouter02: '',
  smartRouter: '',
  universalRouter: 'Universal Router',
  factory: 'Factory',
  poolManager: 'Pool Manager',
  poolMaster: 'Pool Master'
}

/** `someNewRole` -> `Some New Role`, so an unknown role still reads as English. */
const humanizeRole = (role: string): string =>
  role
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()

export const getDexRouterLabel = (providerName: string, role: string): string => {
  const suffix = role in ROLE_SUFFIXES ? ROLE_SUFFIXES[role] : humanizeRole(role)
  return suffix ? `${providerName} ${suffix}` : providerName
}

export const getDexRouterKey = (chainId: bigint | number, address: string): string =>
  `${chainId.toString()}:${address.toLowerCase()}`

export function buildDexRouterIndex(response: DexRoutersApiResponse[]): DexRouterIndex {
  const index: DexRouterIndex = {}

  response.forEach((chain) => {
    if (!chain?.chainId || !Array.isArray(chain.dexes)) return

    chain.dexes.forEach((dex) => {
      if (!dex?.providerName || !dex.contractAddress) return

      Object.entries(dex.contractAddress).forEach(([role, address]) => {
        if (typeof address !== 'string' || !address.startsWith('0x')) return

        index[getDexRouterKey(chain.chainId, address)] = {
          name: getDexRouterLabel(dex.providerName, role),
          providerName: dex.providerName,
          role,
          ...(dex.providerLogoURI ? { logoURI: dex.providerLogoURI } : {})
        }
      })
    })
  })

  return index
}

/**
 * Routers the wallet must name even when the backend registry is missing,
 * stale or unreachable (it is fetched once a day and only cached afterwards).
 * Same shape as the API response so it goes through the same label rules; the
 * fetched registry still wins for any address it also lists.
 *
 * `PulseXSwapRouter` (0xDA9aBA…) is what app.pulsex.com sends every swap
 * through (`multicall`), and the backend registry only lists the V2 router, so
 * a PulseX swap rendered as "Multicall on 0xDA9aBA…B23Bba" (#577). Addresses
 * checked against the verified contract names on scan.pulsechain.com.
 */
export const BUNDLED_DEX_ROUTERS: DexRoutersApiResponse[] = [
  {
    chainId: 369,
    chainName: 'PulseChain',
    dexes: [
      {
        // Routes V1, V2 and stable pools in one call, so it is just "PulseX".
        providerName: 'PulseX',
        providerLogoURI: 'https://icons.llamao.fi/icons/protocols/pulsex',
        contractAddress: { swapRouter: '0xDA9aBA4eACF54E0273f56dfFee6B8F1e20B23Bba' }
      },
      {
        providerName: 'PulseX V1',
        providerLogoURI: 'https://icons.llamao.fi/icons/protocols/pulsex',
        contractAddress: {
          factory: '0x1715a3E4A142d8b698131108995174F37aEBA10D',
          router02: '0x98bf93ebf5c380C0e6Ae8e192A7e2AE08edAcc02'
        }
      },
      {
        providerName: 'PulseX V2',
        providerLogoURI: 'https://icons.llamao.fi/icons/protocols/pulsex',
        contractAddress: {
          factory: '0x29eA7545DEf87022BAdc76323F373EA1e707C523',
          router02: '0x165C3410fC91EF562C50559f7d2289fEbed552d9'
        }
      }
    ]
  }
]

export async function fetchDexRouterIndex(fetchFn: Fetch): Promise<DexRouterIndex> {
  const res = await fetchFn(DEX_ROUTERS_URL)

  if (!res.ok) throw new Error(`Dex routers request failed with status ${res.status}`)

  const json = await res.json()

  if (!Array.isArray(json)) throw new Error('Dex routers response is not a list')

  return buildDexRouterIndex(json as DexRoutersApiResponse[])
}

/** {@link BUNDLED_DEX_ROUTERS}, indexed once at import. */
export const BUNDLED_DEX_ROUTER_INDEX: DexRouterIndex = buildDexRouterIndex(BUNDLED_DEX_ROUTERS)
